import { Menu, MenuList } from '@chakra-ui/react'
import { Token, Width } from 'types/chakra'
import { IAsyncSelectOption, IAsyncSelectOptionWithIcon } from 'interfaces/common'
import DropdownButton from './DropdownButton'
import DropdownSelection from './DropdownSelection'
import { uniqBy } from 'lodash'
import { DARK_PAGE_BG, DARK_TABLE_BORDER } from 'constants/dark-tokens'

interface IDropdownProps {
  name: string
  items: IAsyncSelectOptionWithIcon<number>[]
  options: IAsyncSelectOptionWithIcon<number>[]
  setItems: (options: IAsyncSelectOptionWithIcon<number>[]) => void
  width?: Token<Width | number, 'sizes'>
  height?: Token<Width | number, 'sizes'>
  placeHolder?: string
  isDisabled?: boolean
  /** When true applies dark surface palette — leave false (default) for light contexts */
  dark?: boolean
}

/**
 * Multi-select filter dropdown (Created by, Tag, …). The panel always matches the
 * trigger's width: these controls sit in a row, and a menu sized to its longest
 * option reads as an unrelated width. Long labels ellipsise instead.
 */
const Dropdown = (props: IDropdownProps) => {
  const { name, items, options, setItems, width, placeHolder, height, isDisabled, dark = false } = props

  // Selected entries stay listed even when the current options no longer carry them.
  const sortedOptions = uniqBy([...items, ...options], 'value')

  const handleSelectOption = (option: IAsyncSelectOption<number>) => {
    setItems(
      items.some(item => item.value === option.value)
        ? items.filter(item => item.value !== option.value)
        : [...items, option]
    )
  }

  return (
    <Menu closeOnSelect autoSelect={false} matchWidth strategy="fixed" isLazy>
      <DropdownButton
        items={items}
        placeHolder={placeHolder}
        width={width}
        height={height}
        isDisabled={isDisabled}
        dark={dark}
      />
      <MenuList
        zIndex="1001"
        minWidth="auto"
        maxHeight="300px"
        overflowY="auto"
        border={dark ? `1px solid ${DARK_TABLE_BORDER}` : '1px solid #E2E8F0'}
        bg={dark ? DARK_PAGE_BG : undefined}
        boxShadow={dark ? '0px 8px 24px rgba(0,0,0,0.4)' : undefined}
        hidden={sortedOptions.length === 0}
      >
        {sortedOptions.map((option, index) => (
          <DropdownSelection
            key={`${name}-${index}`}
            item={option}
            isSelected={items.some(item => item.value === option.value)}
            onClick={() => handleSelectOption(option)}
            height={height}
            dark={dark}
          />
        ))}
      </MenuList>
    </Menu>
  )
}

export default Dropdown
